<template>
  <component :is="useCard">
    <div class="map-layout">
      <div class="map-scroll">
        <div class="map" :style="{ width: `${W}px`, height: `${H}px` }">
          <svg class="edges" :width="W" :height="H">
            <defs>
              <marker v-for="(color, key) in colors" :key="key" :id="`eco850-arr-${key}`"
                      viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" :fill="color"/>
              </marker>
            </defs>
            <path v-for="(edge, index) in edges" :key="index"
                  :d="edge.d" fill="none"
                  :stroke="colors[edge.c]" :stroke-width="edge.w || 1.5"
                  :stroke-dasharray="edge.dash"
                  :marker-end="edge.arrow ? `url(#eco850-arr-${edge.c})` : null"
                  :class="{ dim: hoverKey && edge.k !== hoverKey }"/>
          </svg>

          <div v-for="header in headers" :key="header.text" class="col-header" :style="rect(header)">
            {{ header.text }}<br>{{ header.sub }}
          </div>

          <div v-for="node in nodes" :key="node.code" class="node" :class="[node.cls, { dim: nodeDimmed(node.code) }]"
               :style="rect(node)" @click="onClickNode(node.code)">
            {{ node.label }}<br>({{ node.code }})
          </div>

          <!-- 생산품 -->
          <div class="product-table" :class="{ dim: !!hoverKey }" :style="rect(product)">
            <div v-for="cell in product.cells" :key="cell.code" class="cell" @click="onClickNode(cell.code)">
              {{ cell.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- 범례: 도면 우측에서 스크롤을 따라다님 -->
      <div class="legend-wrap">
        <div class="legend">
          <h1>※ 범 례</h1>
          <div class="legend-grid">
            <div v-for="item in legendItems" :key="item.key" class="legend-row cursor-pointer"
                 :class="{ dim: hoverKey && hoverKey !== item.key }"
                 @mouseenter="hoverKey = item.key" @mouseleave="hoverKey = ''">
              <div class="name">{{ item.name }}</div>
              <div class="line">
                <svg width="90" height="10">
                  <path d="M 0 5 H 82" fill="none" :stroke="colors[item.c]" stroke-width="1.5"
                        :stroke-dasharray="item.dash" :marker-end="`url(#eco850-arr-${item.c})`"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
export default {
  props: {
    card: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    useCard() {
      if (this.card) {
        return 'vx-card'
      }

      return 'div'
    }
  },
  data() {
    return {
      W: 1000,
      H: 720,
      colors: {
        red: '#FF0000',
        blue: '#0000FF',
        green: '#00B050',
        orange: '#FFA500',
        black: '#000000',
      },
      headers: [
        { text: 'PU공정', sub: '(유틸리티공정)', x: 40, y: 8, w: 180, h: 50 },
        { text: 'P공정', sub: '(제품제조공정)', x: 400, y: 8, w: 230, h: 50 },
        { text: 'PW공정', sub: '(환경오염물질 처리공정)', x: 740, y: 8, w: 230, h: 50 },
        { text: 'PE공정', sub: '(기타 공정)', x: 760, y: 390, w: 200, h: 50 },
      ],
      nodes: [
        { code: 'PU-01', label: '자재입고공정', x: 40, y: 75, w: 180, h: 50 },
        { code: 'PU-02', label: '용수공급공정', x: 40, y: 150, w: 180, h: 50 },
        { code: 'PU-03', label: '사용물질저장공정', x: 40, y: 225, w: 180, h: 50 },
        { code: 'PU-04', label: '함침전극봉공정', x: 40, y: 300, w: 180, h: 50 },
        { code: 'PU-05', label: '산소공정', x: 40, y: 375, w: 180, h: 50 },
        { code: 'PU-06', label: 'ROLL연마공정', x: 40, y: 455, w: 180, h: 50 },
        { code: 'PU-07', label: '공기압축공정', x: 40, y: 535, w: 180, h: 50 },
        { code: 'PU-08', label: '기체연료사용공정', x: 40, y: 615, w: 180, h: 50 },

        { code: 'P-01', label: '용해공정', x: 400, y: 75, w: 230, h: 75, cls: 'gray' },
        { code: 'P-02', label: '정련공정', x: 400, y: 190, w: 230, h: 75, cls: 'gray' },
        { code: 'P-03', label: '연속주조공정', x: 400, y: 305, w: 230, h: 75, cls: 'gray' },
        { code: 'P-04', label: '압연공정', x: 400, y: 420, w: 230, h: 75, cls: 'gray' },
        { code: 'P-05', label: '이화학시험공정', x: 400, y: 630, w: 230, h: 45 },

        { code: 'PW-01', label: '폐수처리공정', x: 760, y: 75, w: 200, h: 48 },
        { code: 'PW-02', label: '폐기물보관공정', x: 760, y: 145, w: 200, h: 48 },
        { code: 'PW-03', label: '슬래그처리공정', x: 760, y: 215, w: 200, h: 48 },
        { code: 'PW-04', label: '비점오염저감공정', x: 760, y: 285, w: 200, h: 48 },

        { code: 'PE-01', label: '오수처리공정', x: 760, y: 460, w: 200, h: 48 },
      ],
      product: {
        x: 550, y: 510, w: 140, h: 50,
        cells: [
          { label: '생산품', code: 'P-04_생산품' },
          { label: '처리', code: 'P-04_처리' },
          { label: '봉강', code: 'P-04_봉강' },
          { label: '판매', code: 'P-04_판매' },
        ]
      },
      legendItems: [
        { key: 'material', name: '자재', c: 'red' },
        { key: 'water', name: '용수', c: 'blue' },
        { key: 'waste', name: '폐기물', c: 'orange', dash: '6 4' },
        { key: 'sewage', name: '폐수', c: 'black', dash: '6 4' },
        { key: 'air', name: 'Air', c: 'green' },
      ],
      // 범례 hover 시 강조할 관련 공정 (카테고리 → 공정코드)
      related: {
        material: ['PU-01', 'PU-03', 'PU-04', 'PU-05', 'PU-06', 'P-01', 'P-02', 'P-04'],
        water: ['PU-02', 'P-01', 'P-02', 'P-03', 'P-04', 'P-05', 'PW-01', 'PW-03'],
        waste: ['P-01', 'P-02', 'P-03', 'PW-02'],
        sewage: ['P-03', 'P-04', 'PW-01', 'PW-04'],
        air: ['PU-07', 'P-01', 'P-02', 'P-03', 'P-04'],
      },
      hoverKey: '',
      edges: [
        // 자재 (적색 실선)
        // 자재입고공정 → 용해공정 (청색 세로선은 점프로 통과)
        { k: 'material', c: 'red', d: 'M 220 100 H 274 A 6 6 0 0 1 286 100 H 396', arrow: true },
        // 사용물질저장·함침전극봉·산소공정 합류(x=250) → 용해공정 + 정련공정
        { k: 'material', c: 'red', d: 'M 220 250 H 250' },
        { k: 'material', c: 'red', d: 'M 220 325 H 250' },
        { k: 'material', c: 'red', d: 'M 220 400 H 250' },
        { k: 'material', c: 'red', d: 'M 250 118 V 400' },
        { k: 'material', c: 'red', d: 'M 250 118 H 274 A 6 6 0 0 1 286 118 H 396', arrow: true },
        { k: 'material', c: 'red', d: 'M 250 215 H 274 A 6 6 0 0 1 286 215 H 304 A 6 6 0 0 1 316 215 H 396', arrow: true },
        // ROLL연마공정 → 압연공정
        { k: 'material', c: 'red', d: 'M 220 480 H 274 A 6 6 0 0 1 286 480 H 304 A 6 6 0 0 1 316 480 H 340 V 445 H 396', arrow: true },

        // 용수 (청색 실선)
        { k: 'water', c: 'blue', d: 'M 220 175 H 244 A 6 6 0 0 1 256 175 H 280' },
        { k: 'water', c: 'blue', d: 'M 280 66 V 652' },
        { k: 'water', c: 'blue', d: 'M 280 132 H 396', arrow: true },
        { k: 'water', c: 'blue', d: 'M 280 240 H 304 A 6 6 0 0 1 316 240 H 396', arrow: true },
        { k: 'water', c: 'blue', d: 'M 280 342 H 304 A 6 6 0 0 1 316 342 H 396', arrow: true },
        { k: 'water', c: 'blue', d: 'M 280 460 H 304 A 6 6 0 0 1 316 460 H 396', arrow: true },
        { k: 'water', c: 'blue', d: 'M 280 652 H 396', arrow: true },
        { k: 'water', c: 'blue', d: 'M 280 66 H 725 V 235 H 756', arrow: true },
        { k: 'water', c: 'blue', d: 'M 725 92 H 756', arrow: true },

        // Air (녹색 실선)
        { k: 'air', c: 'green', d: 'M 220 560 H 274 A 6 6 0 0 1 286 560 H 310' },
        { k: 'air', c: 'green', d: 'M 310 145 V 560' },
        { k: 'air', c: 'green', d: 'M 310 145 H 396', arrow: true },
        { k: 'air', c: 'green', d: 'M 310 252 H 396', arrow: true },
        { k: 'air', c: 'green', d: 'M 310 355 H 396', arrow: true },
        { k: 'air', c: 'green', d: 'M 310 475 H 334 A 6 6 0 0 1 346 475 H 396', arrow: true },

        // 공정 간 연결 및 생산품 (흑색 실선, 범례 없음)
        { k: 'flow', c: 'black', d: 'M 515 150 V 190', w: 2 },
        { k: 'flow', c: 'black', d: 'M 515 265 V 305', w: 2 },
        { k: 'flow', c: 'black', d: 'M 515 380 V 420', w: 2 },
        { k: 'flow', c: 'black', d: 'M 515 495 V 535 H 544', w: 2, arrow: true },

        // 폐기물 (주황색 점선)
        { k: 'waste', c: 'orange', d: 'M 630 112 H 690', dash: '6 4' },
        { k: 'waste', c: 'orange', d: 'M 630 227 H 690', dash: '6 4' },
        { k: 'waste', c: 'orange', d: 'M 630 342 H 690 V 112', dash: '6 4' },
        { k: 'waste', c: 'orange', d: 'M 690 169 H 719 A 6 6 0 0 1 731 169 H 756', dash: '6 4', arrow: true },

        // 폐수 (흑색 점선)
        { k: 'sewage', c: 'black', d: 'M 630 365 H 710', dash: '6 4' },
        { k: 'sewage', c: 'black', d: 'M 630 480 H 710 V 108 H 719 A 6 6 0 0 1 731 108 H 756', dash: '6 4', arrow: true },
        { k: 'sewage', c: 'black', d: 'M 710 309 H 756', dash: '6 4', arrow: true },
      ],
    }
  },
  methods: {
    isRelated(code) {
      const codes = this.related[this.hoverKey]
      return codes ? codes.includes(code) : true
    },
    nodeDimmed(code) {
      return !!this.hoverKey && !this.isRelated(code)
    },
    rect(o) {
      return {
        left: `${o.x}px`,
        top: `${o.y}px`,
        width: `${o.w}px`,
        height: `${o.h}px`,
      }
    },
    onClickNode(itemId) {
      this.$emit('node', itemId)
    }
  }
}
</script>

<style lang="scss" scoped>
.map-layout {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 2rem 1rem 4rem;
}
.map-scroll {
  min-width: 0;
  overflow-x: auto;
}
.map {
  position: relative;
}
.edges {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;

  path { transition: opacity 0.15s }
  path.dim { opacity: 0.08 }
}
.node, .product-table { transition: opacity 0.15s }
.node.dim, .product-table.dim { opacity: 0.2 }
.col-header {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}
.node {
  position: absolute;
  padding: 4px;
  display: flex;
  flex-direction: column;
  border: 1px solid #333333;
  background-color: #ffffff;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 13px;
  line-height: 1.3;
  cursor: pointer;

  &.gray { background-color: #D9D9D9 }

  &:hover { background-color: #E5E5E5; border: 1px solid #000000 !important; }
}
.product-table {
  position: absolute;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid #333333;
  border-left: 1px solid #333333;

  .cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #333333;
    border-bottom: 1px solid #333333;
    background-color: #ffffff;
    font-size: 13px;
    cursor: pointer;

    &:hover { background-color: #E5E5E5 }
  }
}
.legend-wrap {
  flex: 0 0 300px;
}
.legend {
  position: sticky;
  top: 100px; // 상단 고정 헤더 높이만큼 띄움
  border: 1px solid #333333;
  background-color: #ffffff;
  padding: 8px 12px;

  h1 {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .legend-grid {
    display: grid;
    row-gap: 2px;
  }
  .legend-row {
    display: grid;
    grid-template-columns: 1fr 96px;
    align-items: center;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: opacity 0.15s, background-color 0.15s;

    &:hover { background-color: #F3F4F6 }
    &.dim { opacity: 0.35 }

    .name { font-size: 12px }
    .line { display: flex; align-items: center; justify-content: flex-end }
  }
}
</style>
